import { FC, useCallback, useMemo } from "react";

import { Box } from "@mui/material";
import { useController, UseFormReturn } from "react-hook-form";

import { NewsletterFormSignupData, SignupCommunicationPreference } from "../types";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardCheckboxGroup } from "@/dashboard-design-system/DashboardCheckboxGroup";

// The group spaces its rows 4px apart; the design puts 8px between them
const OPTION_ROW_STYLE = { marginTop: 8 } as const;

type NewsletterFormCommunicationPreferencesProps = {
  form: UseFormReturn<NewsletterFormSignupData>;
  communicationPreferences: Array<{
    id: string;
    label: string;
    contactListId: string;
  }>;
  isLegacyCommunicationPreferences?: boolean;
  communicationPreferencesTitle?: string | null;
  organizationName: string;
  previewConfiguration?: {
    isPreview: boolean;
  };
};

export const NewsletterFormCommunicationPreferences: FC<NewsletterFormCommunicationPreferencesProps> = ({
  form,
  communicationPreferences,
  isLegacyCommunicationPreferences = false,
  communicationPreferencesTitle,
  organizationName,
  previewConfiguration,
}) => {
  const { t } = useTranslate();

  const { field, fieldState } = useController({
    control: form.control,
    name: "communicationPreferencesSelected",
    rules: {
      validate: {
        nonEmpty: (value: NewsletterFormSignupData["communicationPreferencesSelected"]) =>
          Array.isArray(value) && value.length > 0 ? true : t("common", "isRequired"),
      },
    },
  });

  const options = useMemo(
    () => communicationPreferences.filter((option) => option.label.trim() !== ""),
    [communicationPreferences]
  );

  // Legacy options carry the admin's own wording, so only list names give way to the single question.
  const isSingleQuestion = options.length === 1 && !isLegacyCommunicationPreferences;
  const groupTitle = isLegacyCommunicationPreferences
    ? t("dashboard", "newsletterForms.signUp.communicationPreferences")
    : communicationPreferencesTitle ||
      t("common", "communicationPreferences.defaultQuestionTitle", { organizationName });

  const checkboxes = useMemo(
    () =>
      options.map((option, optionIndex) => ({
        choice: option.id,
        label: isSingleQuestion ? groupTitle : option.label,
        clickableLabel: true,
        ...(optionIndex > 0 && { style: OPTION_ROW_STYLE }),
      })),
    [options, isSingleQuestion, groupTitle]
  );

  const values = useMemo(() => {
    const selected = Array.isArray(field.value) ? field.value : [];
    return Object.fromEntries(options.map((option) => [option.id, selected.some((item) => item.value === option.id)]));
  }, [field.value, options]);

  const handleChange = useCallback(
    (newValues: Record<string, boolean>) => {
      if (previewConfiguration?.isPreview) {
        return;
      }
      const selected: SignupCommunicationPreference[] = options
        .filter((option) => newValues[option.id])
        .map((option) => ({ value: option.id, label: option.label }));

      form.setValue("communicationPreferencesInteracted", true);

      field.onChange(selected);
    },
    [field, form, options, previewConfiguration?.isPreview]
  );

  return (
    // Without the group title, add 8px to the parent gap to match the design's 16px.
    <Box sx={isSingleQuestion ? { marginTop: 1 } : undefined}>
      <DashboardCheckboxGroup
        label={isSingleQuestion ? undefined : groupTitle}
        checkboxes={checkboxes}
        values={values}
        onChange={handleChange}
        // Small shrinks the box and label below the design.
        size="medium"
        inputRef={previewConfiguration?.isPreview ? undefined : field.ref}
        data-test="form-communication-preferences"
        error={Boolean(fieldState.error?.message)}
        errorMessage={fieldState.error?.message}
      />
    </Box>
  );
};
